/*
 * Copyright (c) 2022. American Express Company
 */

// theirs
import * as _ from 'lodash';
import React, { ReactComponentElement } from 'react';
import { LocalDate } from '@js-joda/core';
// ours
import BasicDrawer from '../common/BasicDrawer';
import Typography from '../../../components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import { PersonModel } from 'src/types/person.model';
import ProfilePersonCard from '../../../components/card/ProfilePersonCard';
import PersonDialog from '../../../components/person-dialog/PersonDialog';
import PersonService from '../../../services/person.service';
import LiteralsService from '../../../services/literals/LiteralsService';
import ProfileStatsCard from '../../../components/card/ProfileStatsCard';
import { FullUserModel } from 'src/types/user.model';
import CashFlowService from '../../../services/cashflow.service';
import {
    ButtonPrimary,
    Collapsible,
    CollapsibleContent,
    CollapsiblePanel,
    GlyphRight,
    IconBank,
    IconBarChart,
    IconBilling,
    IconCards,
    IconHide,
    IconHome,
    IconOpenBanking,
    IconProcessing
} from '@americanexpress/dls-react';
// styles and icons
import styles from './FinancialProfileDrawer.module.scss';
import { StatsCardColour } from 'src/types/view-models/stats-card.view.model';
import CalcFactory from 'src/bw/factories/calc.factory';
import FormatService from '../../../services/format.service';
import NavigationService from '../../../services/navigation.service';
import { CashFlowModel } from 'src/types/cash-flow.model';
import ActionCard from '../../../components/card/ActionCard';
import { AccountModel } from 'src/types/account.model';
import { DebtModel } from 'src/types/debt.model';
import { InvestmentHoldingModel } from 'src/types/investment-holding.model';
import TimeAgoService from '../../../services/time-ago.service';
import { RealEstateModel } from 'src/types/real-estate.model';
import InfoCard from '../../../components/card/InfoCard';
import { HELP_DOMAIN } from 'src/utils/constants';
import { InstitutionModel } from 'src/types/institution.model';
import { BwAlertType } from 'src/types/view-models/bw-alert.view.model';
import BwAlert from 'src/components/alert/BwAlert';
import WideView from 'src/components/atomic/layouts/wideView/WideView';
import Anchor from 'src/components/atomic/atoms/anchor/Anchor';
import BackgroundBanner from 'src/components/atomic/atoms/backgroundBanner/BackgroundBanner';

interface CollapsibleAccountsBarProps {
    id: string;
    accounts: AccountModel[] | RealEstateModel[] | InvestmentHoldingModel[] | DebtModel[];
    total?: string;
    headerText: string;
    icon: JSX.Element;
    children: JSX.Element;
    contentDataCy?: string;
    holdings?: InvestmentHoldingModel[];
}

const collapsibleBorder = '0.0625rem solid #ecedee';

const CollapsibleAccountsBar = ({
    id,
    accounts,
    total,
    headerText,
    icon,
    children,
    contentDataCy,
    holdings
}: CollapsibleAccountsBarProps): JSX.Element => {
    return (
        <Collapsible className={styles.accountsCollapsible} defaultOpen={[id]} theme={{ border: collapsibleBorder }}>
            <CollapsiblePanel icon={icon} id={id} className={styles.accountsCollapsibleCategoryHeader}>
                <div className={styles.accountsCollapsibleCategoryHeaderInner}>
                    <Typography
                        variant={TypographyVariants.HEADING_XS}
                        className={styles.accountsCollapsibleCategoryHeaderText}
                    >
                        {headerText}
                    </Typography>
                    {total && (
                        <Typography
                            component="p"
                            variant={TypographyVariants.HEADING_XS}
                            className={styles.accountsCollapsibleCategoryHeaderText}
                        >
                            {total}
                        </Typography>
                    )}
                </div>
            </CollapsiblePanel>
            {(accounts.length > 0 || holdings?.length > 0) && (
                <CollapsibleContent labelledBy={id} data-cy={contentDataCy}>
                    {children}
                </CollapsibleContent>
            )}
        </Collapsible>
    );
};

export default class FinancialProfileDrawer extends BasicDrawer<
    any,
    {
        showHidden: boolean;
        openPersonDialog: boolean;
        openTaxDialog: boolean;
        currentPerson: PersonModel;
        taxStatus: string;
        taxLocation: string;
        isImageEditing: boolean;
    }
> {
    public override state: any = {
        showHidden: false,
        openPersonDialog: false,
        openTaxDialog: false,
        currentPerson: null,
        taxStatus: null,
        taxLocation: null,
        isImageEditing: false
    };

    public override componentDidMount(): void {
        const mentorCondition: boolean =
            this.props.user.accounts.length === 0 && !this.props.user.attributes.metProfile;
        if (mentorCondition) {
            this.props.processAction('meet-your-profile');
            this.props.handleUpdateUserAttribute('metProfile', true);
        }
    }

    public override render(): ReactComponentElement<any> {
        const user: FullUserModel = this.props.user;
        const accounts: AccountModel[] = this.props.drawerProps.accounts;
        const banking: AccountModel[] = _.filter(accounts, (account) => account.type === 'depository');
        const credit: AccountModel[] = _.filter(accounts, (account) => account.type === 'credit');
        const investment: AccountModel[] = _.filter(accounts, (account) => account.type === 'investment');
        const realEstate: RealEstateModel[] = this.props.user.realEstate;
        const debts: DebtModel[] = _.filter(this.props.user.debts, (debt) => !!debt.id);
        const cryptoHoldings: InvestmentHoldingModel[] = _.filter(
            this.props.user.holdings,
            (it) => it.type === 'crypto'
        );
        const otherHoldings: InvestmentHoldingModel[] = _.filter(
            this.props.user.holdings,
            (it) => it.type !== 'crypto'
        );
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const hiddenAccounts: AccountModel[] = this.props.drawerProps.hiddenAccounts;
        const disconnectedInstitutions: InstitutionModel[] = _.filter(this.props.user.institutions, (institution) => {
            return institution.loginRequired === true;
        });

        const offset: number = CashFlowService.getCashFlowIndexWithDate(user.rawCashFlow, LocalDate.now());
        const netWorth: number = CalcFactory.getNetWorth(user.rawCashFlow[offset]);
        const startDate: LocalDate = user.rawCashFlow[offset - 12].startDate;
        const endDate: LocalDate = user.rawCashFlow[offset - 1].endDate;
        const incomes: number = _.sumBy(user.cashFlow, 'income');
        const expenses: number = _.sumBy(user.cashFlow, 'expense');

        let income: number = 0;
        let expense: number = 0;

        if (expenses || incomes) {
            for (let i = 1; i < 13; i++) {
                const cashFlow: CashFlowModel = user.rawCashFlow[offset - i];
                income += cashFlow.income;
                expense += cashFlow.expense;
            }
        }

        const securityUrl: string = HELP_DOMAIN ? HELP_DOMAIN + '/articles/data-security' : null;

        return (
            <WideView
                classNameView={styles.financialProfileContainer}
                title={LiteralsService.getLiteral('FinancialProfile.PageTitle')}
                hasBackgroundBanner
                showFooter
                data-cy="financialProfileContainer"
                history={this.props.drawerProps.history}
                handleBack={this.props.handleTopDrawer}
                headerChildren={
                    <InfoCard
                        title={LiteralsService.getLiteral('FinancialProfile.HeaderInfoCardTitle')}
                        subtitle={LiteralsService.getLiteral('FinancialProfile.HeaderInfoCardSubtitle')}
                        url={securityUrl}
                    />
                }
            >
                {this.state.currentPerson && this.state.openPersonDialog && (
                    <PersonDialog
                        person={this.state.currentPerson}
                        user={this.props.user}
                        updateUserLocally={this.props.updateUserLocally}
                        open={this.state.openPersonDialog}
                        updatePersons={this.updatePersons.bind(this)}
                        updatePerson={this.updatePerson.bind(this)}
                        close={() => {
                            this.setState({
                                openPersonDialog: false
                            });
                        }}
                    />
                )}
                <div className={styles.innerFinanceContent}>
                    <BackgroundBanner>
                        <div className={[styles.peopleContainer, styles.financeContainer].join(' ')}>
                            <Typography
                                component="h2"
                                variant={TypographyVariants.HEADING_S}
                                className={styles.financeHeading}
                            >
                                {LiteralsService.getLiteral(
                                    this.props.drawerProps.persons.length > 1
                                        ? 'FinancialProfile.PeopleHeadingFamily'
                                        : 'FinancialProfile.PeopleHeadingSingle'
                                )}
                            </Typography>
                            <div className={styles.peopleRow}>
                                {this.props.drawerProps.persons.map((person: PersonModel, index: number) => {
                                    return (
                                        <ProfilePersonCard
                                            person={person}
                                            onClick={() => {
                                                this.setState({
                                                    currentPerson: person,
                                                    openPersonDialog: true
                                                });
                                            }}
                                            key={index}
                                            index={index}
                                        />
                                    );
                                })}
                            </div>
                            <div className={styles.addMoreRow} onClick={this.startAddPerson.bind(this)}>
                                <Anchor
                                    typographyProps={{
                                        variant: TypographyVariants.BODY,
                                        className: styles.addFamilyCallout
                                    }}
                                >
                                    {LiteralsService.getLiteral('FinancialProfile.AddFamilyLinkText')}
                                </Anchor>
                                <GlyphRight className={styles.addFamilyArrowRight} />
                            </div>
                        </div>
                        <div className={[styles.overviewContainer, styles.financeContainer].join(' ')}>
                            <div className={styles.overviewTopColumn}>
                                <Typography
                                    component="h2"
                                    variant={TypographyVariants.HEADING_S}
                                    className={styles.financeHeading}
                                >
                                    {LiteralsService.getLiteral('FinancialProfile.OverviewHeading')}
                                </Typography>
                                <Typography variant={TypographyVariants.SUBTEXT}>
                                    {LiteralsService.getLiteral('FinancialProfile.OverviewBody')}
                                    <Anchor className={this.props.className}>
                                        {` ${LiteralsService.getLiteral('FinancialProfile.OverviewLinkText')}`}
                                    </Anchor>
                                </Typography>
                            </div>
                            <div className={styles.overviewBottomRow}>
                                <ProfileStatsCard
                                    onClick={async () =>
                                        await NavigationService.goTo(
                                            this.props.drawerProps.history,
                                            '/drawer/add-accounts',
                                            'Add accounts drawer'
                                        )
                                    }
                                    colour={StatsCardColour.WHITE}
                                    topText={LiteralsService.getLiteral(
                                        this.props.drawerProps.persons.length > 1
                                            ? 'FinancialProfile.NetWorthTopTextFamily'
                                            : 'FinancialProfile.NetWorthTopTextSingle'
                                    )}
                                    middleText={FormatService.format(netWorth || 0)}
                                    bottomText={LiteralsService.getLiteral('FinancialProfile.NetWorthBottomText')}
                                    ctaText={LiteralsService.getLiteral('FinancialProfile.NetWorthActionText')}
                                    tooltipText={LiteralsService.getLiteral('FinancialProfile.TooltipText')}
                                />
                                <ProfileStatsCard
                                    onClick={this.props.handleOpenIncomeSummary.bind(this, startDate, endDate)}
                                    colour={StatsCardColour.BLUE}
                                    topText={LiteralsService.getLiteral(
                                        this.props.drawerProps.persons.length > 1
                                            ? 'FinancialProfile.IncomeTopTextFamily'
                                            : 'FinancialProfile.IncomeTopTextSingle'
                                    )}
                                    middleText={FormatService.format(income || 0)}
                                    bottomText={LiteralsService.getLiteral('FinancialProfile.IncomeBottomText')}
                                    ctaText={LiteralsService.getLiteral('FinancialProfile.IncomeActionText')}
                                    tooltipText={LiteralsService.getLiteral('FinancialProfile.TooltipText')}
                                />
                                <ProfileStatsCard
                                    onClick={this.props.handleOpenExpenses.bind(this, startDate, endDate)}
                                    colour={StatsCardColour.DARK_BLUE}
                                    topText={LiteralsService.getLiteral(
                                        this.props.drawerProps.persons.length > 1
                                            ? 'FinancialProfile.ExpenseTopTextFamily'
                                            : 'FinancialProfile.ExpenseTopTextSingle'
                                    )}
                                    middleText={FormatService.format(expense || 0)}
                                    bottomText={LiteralsService.getLiteral('FinancialProfile.ExpenseBottomText')}
                                    ctaText={LiteralsService.getLiteral('FinancialProfile.ExpenseActionText')}
                                    tooltipText={LiteralsService.getLiteral('FinancialProfile.TooltipText')}
                                    data-cy="profileExpensesCard"
                                />
                            </div>
                        </div>
                        <div className={[styles.financialAccountsContainer, styles.financeContainer].join(' ')}>
                            {disconnectedInstitutions.length > 0 && (
                                <div>
                                    {disconnectedInstitutions.length > 1 ? (
                                        <BwAlert
                                            text={LiteralsService.getLiteral(
                                                'FinancialProfile.disconnectedInstitutions',
                                                [disconnectedInstitutions.length]
                                            )}
                                            accentIcon={true}
                                            type={BwAlertType.BAD}
                                            fullWidth={true}
                                            key={'disconnect'}
                                        />
                                    ) : (
                                        <BwAlert
                                            text={LiteralsService.getLiteral(
                                                'FinancialProfile.disconnectedInstitution',
                                                [disconnectedInstitutions[0].name]
                                            )}
                                            accentIcon={true}
                                            action="Reconnect now"
                                            type={BwAlertType.BAD}
                                            fullWidth={true}
                                            onClick={() => {
                                                this.props.handleReconnectInstitution(disconnectedInstitutions[0]);
                                            }}
                                            key={'disconnect'}
                                        />
                                    )}
                                </div>
                            )}
                            <div className={styles.accountsTopColumn}>
                                <Typography
                                    component="h2"
                                    variant={TypographyVariants.HEADING_S}
                                    className={styles.financeHeading}
                                >
                                    {LiteralsService.getLiteral('FinancialProfile.AccountsHeading')}
                                </Typography>
                                <Typography variant={TypographyVariants.SUBTEXT}>
                                    {LiteralsService.getLiteral('FinancialProfile.AccountsBody')}
                                </Typography>
                            </div>
                            <div className={styles.accountsBottomRow}>
                                <div className={styles.accountsCollapsibleContainer}>
                                    <CollapsibleAccountsBar
                                        id="bankingAccounts"
                                        accounts={banking}
                                        total={banking.length && FormatService.format(_.sumBy(banking, 'current') || 0)}
                                        headerText={LiteralsService.getLiteral('FinancialProfile.Deposits')}
                                        icon={<IconBank />}
                                    >
                                        {this.getCollapsibleAccount(banking, [])}
                                    </CollapsibleAccountsBar>
                                    <CollapsibleAccountsBar
                                        id="creditCardAccounts"
                                        accounts={credit}
                                        total={credit.length && FormatService.format(_.sumBy(credit, 'current') || 0)}
                                        headerText={LiteralsService.getLiteral('FinancialProfile.CreditCards')}
                                        icon={<IconCards />}
                                    >
                                        {this.getCollapsibleAccount(credit, [])}
                                    </CollapsibleAccountsBar>
                                    <CollapsibleAccountsBar
                                        id="assetAccounts"
                                        accounts={investment}
                                        total={
                                            investment.length &&
                                            FormatService.format(_.sumBy(investment, 'current') || 0)
                                        }
                                        headerText={LiteralsService.getLiteral('FinancialProfile.Assets')}
                                        icon={<IconBarChart />}
                                        contentDataCy="assetContent"
                                        holdings={otherHoldings}
                                    >
                                        {this.getCollapsibleAccount(investment, otherHoldings)}
                                    </CollapsibleAccountsBar>
                                    <CollapsibleAccountsBar
                                        id="realEstateAccounts"
                                        accounts={realEstate}
                                        total={
                                            realEstate.length &&
                                            FormatService.format(_.sumBy(realEstate, 'amount') || 0)
                                        }
                                        headerText={LiteralsService.getLiteral('FinancialProfile.RealEstate')}
                                        icon={<IconHome />}
                                        contentDataCy="realEstateContent"
                                    >
                                        {this.getCollaspsibleRealEstate(realEstate)}
                                    </CollapsibleAccountsBar>
                                    <CollapsibleAccountsBar
                                        id="debtAccounts"
                                        accounts={debts}
                                        total={debts.length && FormatService.format(_.sumBy(debts, 'amount') || 0)}
                                        headerText={LiteralsService.getLiteral('FinancialProfile.Debt')}
                                        icon={<IconBilling />}
                                        contentDataCy="debtContent"
                                    >
                                        {this.getCollapsibleDebt(debts)}
                                    </CollapsibleAccountsBar>
                                    <CollapsibleAccountsBar
                                        id="cryptoAccounts"
                                        accounts={cryptoHoldings}
                                        total={
                                            cryptoHoldings.length &&
                                            FormatService.format(_.sumBy(cryptoHoldings, 'amount') || 0)
                                        }
                                        headerText={LiteralsService.getLiteral('FinancialProfile.Crypto')}
                                        icon={<IconOpenBanking />}
                                        contentDataCy="cryptoContent"
                                    >
                                        {this.getCollapsibleCrypto(cryptoHoldings)}
                                    </CollapsibleAccountsBar>
                                    <CollapsibleAccountsBar
                                        id="hiddenAccounts"
                                        accounts={hiddenAccounts}
                                        headerText={LiteralsService.getLiteral('FinancialProfile.HiddenAccounts')}
                                        icon={<IconHide />}
                                    >
                                        {this.getCollapsibleAccount(hiddenAccounts, [], true)}
                                    </CollapsibleAccountsBar>
                                </div>
                                <ActionCard
                                    className={styles.connectAccount}
                                    ctaText={LiteralsService.getLiteral('FinancialProfile.ActionBoxCta')}
                                    action={null}
                                >
                                    <Typography
                                        component="p"
                                        variant={TypographyVariants.HEADING_S}
                                        className={styles.financeHeading}
                                    >
                                        {LiteralsService.getLiteral('FinancialProfile.ActionBoxHeading')}
                                    </Typography>
                                    <ButtonPrimary
                                        id="actionCardAddAccountBtn"
                                        onClick={async () =>
                                            await NavigationService.goTo(
                                                this.props.drawerProps.history,
                                                '/drawer/add-accounts',
                                                'Add accounts drawer'
                                            )
                                        }
                                    >
                                        {LiteralsService.getLiteral('FinancialProfile.AddAccountButtonText')}
                                    </ButtonPrimary>
                                </ActionCard>
                            </div>
                        </div>
                    </BackgroundBanner>
                </div>
            </WideView>
        );
    }

    private getCollapsibleAccount(
        accounts: AccountModel[],
        otherHoldings: InvestmentHoldingModel[],
        hidden = false
    ): JSX.Element {
        const accountItems: JSX.Element[] = accounts.map((account: AccountModel) => {
            const sync: JSX.Element = !hidden && (
                <div className={styles.accountsCollapsibleItemInnerRight}>
                    <Typography
                        component="p"
                        variant={TypographyVariants.HEADING_S}
                        className={styles.accountsCollapsibleItemAmount}
                    >
                        {FormatService.format(account.current || 0)}
                    </Typography>
                    <Typography variant={TypographyVariants.CAPTION} className={styles.accountsCollapsibleUpdatedText}>
                        <IconProcessing className={styles.accountsCollapsibleUpdatedIcon} />
                        &nbsp;
                        {TimeAgoService.parse(new Date(account.updated.toString()))}
                    </Typography>
                </div>
            );
            return (
                <div key={`acccount-collapsible-${account.accountId}`} className={styles.accountsCollapsibleItem}>
                    <div className={styles.accountsCollapsibleItemInner}>
                        <div className={styles.accountsCollapsibleItemInnerLeft}>
                            <Anchor
                                onClick={async () => {
                                    await NavigationService.goTo(
                                        this.props.drawerProps.history,
                                        `/drawer/account/${account.accountId}`,
                                        'Account Drawer'
                                    );
                                }}
                                typographyProps={{
                                    variant: TypographyVariants.BODY_BOLD,
                                    className: styles.accountsCollapsibleItemName,
                                    'data-cy': 'accountName'
                                }}
                            >
                                {account.name || account.officialName}
                            </Anchor>
                            <Typography component="p" variant={TypographyVariants.CAPTION}>
                                {account?.institution?.name || account.type}
                            </Typography>
                        </div>
                        {sync}
                    </div>
                </div>
            );
        });

        const holdingItems: JSX.Element[] = otherHoldings.map((holding: InvestmentHoldingModel) => {
            return (
                <div key={`acccount-collapsible-${holding.id}`} className={styles.accountsCollapsibleItem}>
                    <div className={styles.accountsCollapsibleItemInner}>
                        <div className={styles.accountsCollapsibleItemInnerLeft}>
                            <Anchor
                                onClick={async () =>
                                    await NavigationService.goTo(
                                        this.props.drawerProps.history,
                                        `/investment/${holding.id}`,
                                        'Investment Drawer'
                                    )
                                }
                                typographyProps={{
                                    variant: TypographyVariants.BODY_BOLD,
                                    className: styles.accountsCollapsibleItemName
                                }}
                            >
                                {holding.name}
                            </Anchor>
                        </div>
                        <div className={styles.accountsCollapsibleItemInnerRight}>
                            <Typography
                                component="p"
                                variant={TypographyVariants.HEADING_S}
                                className={styles.accountsCollapsibleItemAmount}
                            >
                                {FormatService.format(holding.amount || 0)}
                            </Typography>
                            <Typography
                                variant={TypographyVariants.CAPTION}
                                className={styles.accountsCollapsibleUpdatedText}
                            >
                                <IconProcessing className={styles.accountsCollapsibleUpdatedIcon} />
                                &nbsp;
                                {TimeAgoService.parse(new Date(holding.updated.toString()))}
                            </Typography>
                        </div>
                    </div>
                </div>
            );
        });

        const hiddenAccountInfo: JSX.Element = hidden && (
            <div className={styles.hiddenAccountInfo}>
                <Typography component="p" variant={TypographyVariants.BODY}>
                    {LiteralsService.getLiteral('FinancialProfile.hiddenAccountInfo')}
                </Typography>
            </div>
        );

        return (
            <div className={styles.accountsCollapsibleContent}>
                {hiddenAccountInfo}
                {accountItems}
                {holdingItems}
            </div>
        );
    }

    private getCollapsibleCrypto(cryptoHoldings: InvestmentHoldingModel[]): JSX.Element {
        const accountItems: JSX.Element[] = cryptoHoldings.map((crypto: InvestmentHoldingModel) => {
            return (
                <div key={`acccount-collapsible-${crypto.id}`} className={styles.accountsCollapsibleItem}>
                    <div className={styles.accountsCollapsibleItemInner}>
                        <div className={styles.accountsCollapsibleItemInnerLeft}>
                            <Anchor
                                onClick={async () =>
                                    await NavigationService.goTo(
                                        this.props.drawerProps.history,
                                        `/crypto/${crypto.id}`,
                                        'Crypto Drawer'
                                    )
                                }
                                typographyProps={{
                                    variant: TypographyVariants.BODY_BOLD,
                                    className: styles.accountsCollapsibleItemName
                                }}
                            >
                                {crypto.name}
                            </Anchor>
                            <Typography variant={TypographyVariants.CAPTION}>{crypto.symbol}</Typography>
                        </div>
                        <div className={styles.accountsCollapsibleItemInnerRight}>
                            <Typography
                                component="p"
                                variant={TypographyVariants.HEADING_S}
                                className={styles.accountsCollapsibleItemAmount}
                            >
                                {FormatService.format(crypto.amount || 0)}
                            </Typography>
                            <Typography
                                variant={TypographyVariants.CAPTION}
                                className={styles.accountsCollapsibleUpdatedText}
                            >
                                <IconProcessing className={styles.accountsCollapsibleUpdatedIcon} />
                                &nbsp;
                                {TimeAgoService.parse(new Date(crypto.updated.toString()))}
                            </Typography>
                        </div>
                    </div>
                </div>
            );
        });

        return <div className={styles.accountsCollapsibleContent}>{accountItems}</div>;
    }

    private getCollapsibleDebt(debts: DebtModel[]): JSX.Element {
        const accountItems: JSX.Element[] = debts.map((debt: DebtModel) => {
            return (
                <div key={`acccount-collapsible-${debt.accountId}`} className={styles.accountsCollapsibleItem}>
                    <div className={styles.accountsCollapsibleItemInner}>
                        <div className={styles.accountsCollapsibleItemInnerLeft}>
                            <Anchor
                                onClick={async () =>
                                    await NavigationService.goTo(
                                        this.props.drawerProps.history,
                                        `/debt/${debt.id}`,
                                        'Debt Drawer'
                                    )
                                }
                                typographyProps={{
                                    variant: TypographyVariants.BODY_BOLD,
                                    className: styles.accountsCollapsibleItemName
                                }}
                            >
                                {debt.name}
                            </Anchor>
                            <Typography variant={TypographyVariants.CAPTION}>{debt.type}</Typography>
                        </div>
                        <div className={styles.accountsCollapsibleItemInnerRight}>
                            <Typography
                                component="p"
                                variant={TypographyVariants.HEADING_S}
                                className={styles.accountsCollapsibleItemAmount}
                            >
                                {FormatService.format(debt.amount || 0)}
                            </Typography>
                            <Typography
                                variant={TypographyVariants.CAPTION}
                                className={styles.accountsCollapsibleUpdatedText}
                            >
                                <IconProcessing className={styles.accountsCollapsibleUpdatedIcon} />
                                &nbsp;
                                {TimeAgoService.parse(new Date(debt.updated.toString()))}
                            </Typography>
                        </div>
                    </div>
                </div>
            );
        });

        return <div className={styles.accountsCollapsibleContent}>{accountItems}</div>;
    }

    private getCollaspsibleRealEstate(estates: RealEstateModel[]): JSX.Element {
        const realEstateItems: JSX.Element[] = estates.map((estate: RealEstateModel) => {
            return (
                <div key={`acccount-collapsible-${estate.id}`} className={styles.accountsCollapsibleItem}>
                    <div className={styles.accountsCollapsibleItemInner}>
                        <div className={styles.accountsCollapsibleItemInnerLeft}>
                            <Anchor
                                onClick={async () =>
                                    await NavigationService.goTo(
                                        this.props.drawerProps.history,
                                        `/realestate/${estate.id}`,
                                        'Real Estate'
                                    )
                                }
                                typographyProps={{
                                    variant: TypographyVariants.BODY_BOLD,
                                    className: styles.accountsCollapsibleItemName
                                }}
                            >
                                {estate.name}
                            </Anchor>
                        </div>
                        <div className={styles.accountsCollapsibleItemInnerRight}>
                            <Typography
                                component="p"
                                variant={TypographyVariants.HEADING_S}
                                className={styles.accountsCollapsibleItemAmount}
                            >
                                {FormatService.format(estate.amount || 0)}
                            </Typography>
                        </div>
                    </div>
                </div>
            );
        });

        return <div className={styles.accountsCollapsibleContent}>{realEstateItems}</div>;
    }

    private startAddPerson(): void {
        const person: PersonModel = new PersonModel();
        person.nickname = '';
        person.dob = LocalDate.now();
        person.isDependent = false;

        this.setState({
            openPersonDialog: true,
            currentPerson: person
        });
    }

    private updatePerson(person: PersonModel): void {
        this.setState({
            currentPerson: person
        });
    }

    private async updatePersons(): Promise<void> {
        this.setState({
            openPersonDialog: false,
            currentPerson: null
        });

        const persons: PersonModel[] = await PersonService.getPersons();
        this.props.updateDrawer({
            ...this.props.drawerProps,
            persons
        });

        const newUser = { ...this.props.user, peopleWithPhotos: persons };
        newUser.persons = persons;

        if (this.props.user.currentPerson) {
            const person = _.find(persons, { personUid: this.props.user.currentPerson.personUid });
            if (person) {
                newUser.currentPerson = person;
            }
        }

        this.props.updateUserLocally(newUser);
    }
}
